import Container from "@client/content/Containers/container";
import { SINGLE_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getContainerProps, getRootContainerProps } from "@client/content/lib/helper";
import { type RootContainerProps } from "@client/content/types";
import TextArea from "@client/controls/Inputs/TextArea/component";
import { RatingSurvey } from "@client/controls/Rate/component";
import { parseQueryString } from "@client/lib/api";
import { goTo } from "@client/lib/navigation";
import { type WithIsEditMode } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import { ModuleButton } from "../Button";
import "./style.less";
import { type MemberSurvey } from "./types";

export type MapProps = RootContainerProps &
  WithIsEditMode & {
    questions: MemberSurvey.TicketFeedbackQuestion[];
    answers: MemberSurvey.UserTicketFeedback[];
    fieldStyle?: React.CSSProperties;
    labelStyle?: React.CSSProperties;
    separatorStyle?: React.CSSProperties;
  };

export interface ActionProps {
  onUpdateAnswer: (item: MemberSurvey.View.CreateUpdateUserFeedbackRequest) => void;
  onCreateAnswer: (item: MemberSurvey.View.CreateUpdateUserFeedbackRequest) => void;
}

type AnswerProps = {
  value: string | null;
  onBlur: (answer: string | null) => void;
  fieldStyle?: React.CSSProperties;
};

const Answer = (props: AnswerProps) => {
  const i18n = useI18n();

  const [answer, setAnswer] = React.useState<string | null>(props.value);

  return (
    <TextArea
      style={props.fieldStyle}
      className="form-control"
      value={answer ?? props.value ?? ""}
      onChange={(x) => {
        setAnswer(x);
      }}
      onBlur={() => props.onBlur(answer)}
      placeholder={i18n.addAReply}
      rows={5}
    />
  );
};

const Content = (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  if (!props.questions.length && !props.isEditMode) return null;

  return (
    <>
      {props.questions.map((x, i) => {
        const feedback = props.answers.find((y) => y.ticketFeedbackQuestionId === x.ticketFeedbackQuestionId);
        return (
          <>
            <div className="question_container" key={x.ticketFeedbackQuestionId}>
              <h4 className="question">{x.question}</h4>

              {x.includeRating && (
                <div className="rating">
                  <RatingSurvey
                    onClick={(rating) =>
                      feedback
                        ? props.onUpdateAnswer({ ...feedback, rating: rating })
                        : props.onCreateAnswer({ ...x, rating: rating, answer: null })
                    }
                    value={feedback?.rating ?? null}
                  />
                </div>
              )}

              {((x.includeFreeText && feedback?.rating) || (x.includeFreeText && !x.includeRating)) && (
                <div className="answer">
                  {x.includeRating && (
                    <label style={props.labelStyle} className="wsc_label">
                      {i18n.ratingReason}
                    </label>
                  )}
                  <Answer
                    key={x.ticketFeedbackQuestionId}
                    value={feedback?.answer ?? ""}
                    fieldStyle={props.fieldStyle}
                    onBlur={(answer) =>
                      feedback
                        ? props.onUpdateAnswer({ ...feedback, answer: answer })
                        : props.onCreateAnswer({ ...x, rating: null, answer: answer })
                    }
                  ></Answer>
                </div>
              )}
            </div>
            {props.questions.length - 1 !== i && <div className="separator" style={props.separatorStyle}></div>}
          </>
        );
      })}
      <div style={SINGLE_BUTTONS_STYLE}>
        <ModuleButton
          {...getRootContainerProps(props)}
          onClick={() => goTo(parseQueryString(window.location.href).returnUrl)}
        >
          {i18n.submit}
        </ModuleButton>
      </div>
    </>
  );
};

export default ({ ...props }: ActionProps & MapProps) => (
  <Container {...getContainerProps(props)} className="wsc_content_member_survey">
    <Content {...props} />
  </Container>
);
